import React, { useState, useEffect } from 'react';
import {
    DetailsList,
    DetailsListLayoutMode,
    SelectionMode,
    Stack,
    Text,
    Link,
    Spinner,
    MessageBar,
    MessageBarType,
} from '@fluentui/react';

import {
    useAdminApi,
    platformLabel,
    channelsLabel,
    formatDay,
    formatDateTime,
    formatNumber,
    formatMoney,
    formatRoas,
    downloadCsv,
    sectionStyles,
    statStyles,
    mutedText,
} from '../common';

// แท็บสรุปรายวัน: ค่าโฆษณา, lead (สมัคร + ขอใบเสนอราคา), ลูกค้าจ่ายเงิน, รายได้, CPL / CPA / ROAS จาก GET /marketing/summary

const number = (fieldName, name, format = formatNumber) => ({
    key: fieldName, name, fieldName, minWidth: 80, maxWidth: 110,
    onRender: (item) => <Text>{format(item[fieldName])}</Text>,
});

// คอลัมน์ตัวเลขใช้ร่วมกันทั้งรายวัน / ตามแพลตฟอร์ม / ตามแคมเปญ
function metricColumns() {
    return [
        number('spend', 'ค่าโฆษณา', formatMoney),
        number('clicks', 'clicks'),
        number('signups', 'สมัคร'),
        number('quotations', 'ใบเสนอราคา'),
        number('leads', 'lead'),
        number('cpl', 'ต้นทุน/lead', formatMoney),
        number('paid', 'จ่ายเงิน'),
        number('revenue', 'รายได้ก่อน VAT', formatMoney),
        number('cpa', 'ต้นทุน/ลูกค้า', formatMoney),
        number('roas', 'ROAS', formatRoas),
    ];
}

const dailyColumns = [
    { key: 'date', name: 'วันที่', minWidth: 100, maxWidth: 120, onRender: (item) => <Text>{formatDay(item.date)}</Text> },
    ...metricColumns(),
];

const platformColumns = [
    { key: 'platform', name: 'แพลตฟอร์ม', minWidth: 160, maxWidth: 220, onRender: (item) => <Text>{platformLabel(item.platform)}</Text> },
    ...metricColumns(),
];

const campaignColumns = [
    {
        key: 'name', name: 'แคมเปญ', minWidth: 200, maxWidth: 280, isResizable: true,
        onRender: (item) => (
            <Stack>
                <Text>{item.name}</Text>
                <Text variant="small" styles={item.registered ? mutedText : { root: { color: '#a4262c' } }}>
                    {platformLabel(item.platform)}{item.registered ? '' : ' · ยังไม่ลงทะเบียน'}
                </Text>
            </Stack>
        ),
    },
    ...metricColumns(),
    {
        key: 'targetCpa', name: 'เป้าต้นทุน/lead', minWidth: 100, maxWidth: 120,
        onRender: (item) => {
            const over = item.targetCpa && item.cpl && item.cpl > item.targetCpa * 1.3;
            return <Text styles={over ? { root: { color: '#a4262c', fontWeight: 600 } } : undefined}>{formatMoney(item.targetCpa)}</Text>;
        },
    },
];

// แคมเปญ content (ไม่ใช่โฆษณา): ต้นทุนผลิตแทนค่าโฆษณา ไม่มี clicks
const contentColumns = [
    {
        key: 'name', name: 'แคมเปญ content', minWidth: 200, maxWidth: 280, isResizable: true,
        onRender: (item) => (
            <Stack>
                <Text>{item.name}</Text>
                <Text variant="small" styles={item.registered ? mutedText : { root: { color: '#a4262c' } }}>
                    {item.registered ? channelsLabel(item.channels) : `utm_campaign ${item.utmCampaign} · ยังไม่ลงทะเบียน`}
                </Text>
            </Stack>
        ),
    },
    number('spend', 'ต้นทุนผลิต', formatMoney),
    number('signups', 'สมัคร'),
    number('quotations', 'ใบเสนอราคา'),
    number('leads', 'lead'),
    number('cpl', 'ต้นทุน/lead', formatMoney),
    number('paid', 'จ่ายเงิน'),
    number('revenue', 'รายได้ก่อน VAT', formatMoney),
    number('cpa', 'ต้นทุน/ลูกค้า', formatMoney),
    number('roas', 'รายได้/ต้นทุน', formatRoas),
];

function csvRows(summary) {
    return summary.daily.map((row) => [row.date, row.spend, row.impressions, row.clicks, row.signups, row.quotations, row.leads,
        row.cpl, row.paid, row.revenue, row.cpa, row.roas]);
}

const CSV_HEADER = ['date', 'spend', 'impressions', 'clicks', 'signups', 'quotations', 'leads', 'cpl', 'paid', 'revenue', 'cpa', 'roas'];

const Summary = ({ from, to, refresh }) => {

    const request = useAdminApi();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setLoading(true);
        setError('');

        request('get', '/marketing/summary?from=' + from + '&to=' + to)
            .then((response) => setData(response.data.data))
            .catch((err) => {
                console.log('Summary error: ', err);
                setError('โหลดสรุปรายวันไม่สำเร็จ');
                setData(null);
            })
            .finally(() => setLoading(false));
    }, [request, from, to, refresh]);

    if (loading) return <Spinner label="กำลังโหลด..." />;
    if (error) return <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>;
    if (!data) return null;

    const failedSync = data.sync.filter((item) => !item.ok);

    const stats = [
        { label: 'ค่าโฆษณา (บาท)', value: formatMoney(data.totals.spend) },
        { label: 'lead ทั้งหมด', value: formatNumber(data.totals.leads), note: `สมัคร ${formatNumber(data.totals.signups)} · ใบเสนอราคา ${formatNumber(data.totals.quotations)}` },
        { label: 'lead จากโฆษณา', value: formatNumber(data.paidMedia.leads), note: 'ต้นทุนต่อ lead ' + formatMoney(data.paidMedia.cpl) },
        { label: 'ลูกค้าจ่ายเงิน', value: formatNumber(data.totals.paid), note: `จากโฆษณา ${formatNumber(data.paidMedia.paid)} · ต้นทุนต่อลูกค้า ${formatMoney(data.paidMedia.cpa)}` },
        { label: 'รายได้ก่อน VAT (บาท)', value: formatMoney(data.totals.revenue), note: 'จากโฆษณา ' + formatMoney(data.paidMedia.revenue) },
        { label: 'ROAS โฆษณา', value: formatRoas(data.paidMedia.roas) },
    ];

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            {failedSync.map((item) => (
                <MessageBar key={item.platform} messageBarType={MessageBarType.error}>
                    ดึงข้อมูล {platformLabel(item.platform)} ไม่สำเร็จเมื่อ {formatDateTime(item.time)}: {item.message} ค่าโฆษณาอาจไม่ครบ
                </MessageBar>
            ))}

            <Stack horizontal wrap tokens={{ childrenGap: 16 }}>
                {stats.map((stat) => (
                    <Stack key={stat.label} styles={statStyles}>
                        <Text styles={mutedText}>{stat.label}</Text>
                        <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>{stat.value}</Text>
                        {stat.note && <Text styles={mutedText}>{stat.note}</Text>}
                    </Stack>
                ))}
            </Stack>
            <Text styles={mutedText}>
                lead = สมัครใช้งาน หรือขอใบเสนอราคา ลูกค้าจ่ายเงิน / รายได้ / ROAS นับจาก lead ที่เกิดในช่วงนี้จนถึงวันนี้ (lead ใหม่อาจยังไม่ทันจ่าย ตัวเลขช่วงล่าสุดจึงต่ำ)
                ลูกค้า 1 รายนับรายได้ครั้งเดียวให้ lead แรก ไม่นับบัญชีภายใน / ทดสอบ{data.internalRemoved > 0 ? ` ${formatNumber(data.internalRemoved)} รายการ` : ''}
            </Text>

            <Stack styles={sectionStyles}>
                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>ตามแพลตฟอร์ม</Text>
                <DetailsList items={data.byPlatform} columns={platformColumns} getKey={(item) => item.platform}
                    selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.fixedColumns} />
            </Stack>

            <Stack styles={sectionStyles}>
                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>ตามแคมเปญ</Text>
                {data.byCampaign.length > 0
                    ? <DetailsList items={data.byCampaign} columns={campaignColumns} getKey={(item) => item.key}
                        selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.fixedColumns} />
                    : <Text styles={{ root: { color: '#605E5C', paddingTop: 10 } }}>ยังไม่มีค่าโฆษณาหรือ lead ที่ผูกกับแคมเปญในช่วงนี้</Text>}
            </Stack>

            {data.byContentCampaign && (
                <Stack styles={sectionStyles} tokens={{ childrenGap: 6 }}>
                    <Text variant="large" styles={{ root: { fontWeight: 600 } }}>Content (ไม่ใช่โฆษณา)</Text>
                    <Text styles={mutedText}>
                        lead {formatNumber(data.content.leads)} · จ่ายเงิน {formatNumber(data.content.paid)} · รายได้ {formatMoney(data.content.revenue)} บาท · ต้นทุนผลิต {formatMoney(data.content.spend)} บาท
                        (นับเฉพาะคนที่คลิกลิงก์ที่มี utm_campaign และไม่รวมในตัวเลขโฆษณาด้านบน)
                    </Text>
                    {data.byContentCampaign.length > 0
                        ? <DetailsList items={data.byContentCampaign} columns={contentColumns} getKey={(item) => item.key}
                            selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.fixedColumns} />
                        : <Text styles={{ root: { color: '#605E5C', paddingTop: 10 } }}>ยังไม่มีแคมเปญ content หรือ lead จากลิงก์ content ในช่วงนี้</Text>}
                </Stack>
            )}

            <Stack styles={sectionStyles} tokens={{ childrenGap: 10 }}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                    <Text variant="large" styles={{ root: { fontWeight: 600 } }}>รายวัน</Text>
                    <Link onClick={() => downloadCsv(`marketing-summary-${from}-${to}.csv`, CSV_HEADER, csvRows(data))}>ดาวน์โหลด CSV</Link>
                </Stack>
                <DetailsList items={[...data.daily].reverse()} columns={dailyColumns} getKey={(item) => item.date}
                    selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.fixedColumns} />
            </Stack>
        </Stack>
    );
};

export default Summary;
